// client/src/components/Visualization.jsx
import React, { useState, useMemo } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import * as XLSX from 'xlsx';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement,
    Filler
);

import './Visualization.css';

const Visualization = ({ allData, filters }) => {
    const [studentsChartType, setStudentsChartType] = useState('bar');
    const [coverageChartType, setCoverageChartType] = useState('bar');
    const [subjectsChartType, setSubjectsChartType] = useState('bar');
    const [classesChartType, setClassesChartType] = useState('bar');
    const [providersChartType, setProvidersChartType] = useState('bar');
    const [topSchoolsLimit, setTopSchoolsLimit] = useState(10);
    const [topTeachersLimit, setTopTeachersLimit] = useState(10);
    const [topProvidersLimit, setTopProvidersLimit] = useState(10);

    // Нейтральные цвета для графиков
    const colors = {
        primary: '#4A90A4',      // Спокойный сине-зеленый
        secondary: '#6B9EAA',    // Светлый сине-зеленый
        success: '#5B8C5A',      // Приглушенный зеленый
        warning: '#D4A373',      // Теплый песочный
        danger: '#C17A6E',       // Мягкий терракотовый
        info: '#7D9EA8',         // Серо-голубой
        purple: '#8B7E9E',       // Мягкий сиреневый
        orange: '#E2A76F',       // Мягкий оранжевый
        gray: '#A3B0B4',         // Серый
        light: '#D4DCDE',        // Светло-серый
        dark: '#3A6B7A'          // Темный сине-зеленый
    };

    // Функция для нормализации даты
    const normalizeDate = (dateValue) => {
        if (!dateValue) return null;
        return String(dateValue).substring(0, 10);
    };

    // Фильтрация данных
    const filteredData = useMemo(() => {
        if (!allData?.monitoringData) return [];

        let filtered = [...allData.monitoringData];
        
        if (filters.startDate || filters.endDate) {
            filtered = filtered.filter(record => {
                if (!record.date) return false;
                const recordDate = normalizeDate(record.date);
                let startOk = true;
                let endOk = true;
                if (filters.startDate) startOk = recordDate >= filters.startDate;
                if (filters.endDate) endOk = recordDate <= filters.endDate;
                return startOk && endOk;
            });
        }
        
        if (filters.role !== 'all') {
            filtered = filtered.filter(d => d.role === filters.role);
        }
        
        if (filters.district !== 'all') {
            filtered = filtered.filter(d => d.district === filters.district);
        }
        
        return filtered;
    }, [allData, filters]);

    // 1. Статистика по районам (из таблицы schools)
    const districtUsersData = useMemo(() => {
        const allDistricts = allData?.districts || [];
        const districtMap = new Map();
        
        allDistricts.forEach(district => {
            districtMap.set(district, {
                district: district,
                users: new Set()
            });
        });
        
        filteredData.forEach(record => {
            if (!record.district) return;
            if (districtMap.has(record.district)) {
                districtMap.get(record.district).users.add(record.user_alias);
            }
        });
        
        return Array.from(districtMap.values())
            .map(d => ({
                district: d.district,
                users: d.users.size
            }))
            .sort((a, b) => a.district.localeCompare(b.district));
    }, [filteredData, allData]);

    // 2. Охват школ по районам
    const coverageData = useMemo(() => {
        const schoolsByDistrict = new Map();
        const schoolsWithDataByDistrict = new Map();
        
        allData?.schools.forEach(school => {
            if (!school.district) return;
            if (!schoolsByDistrict.has(school.district)) {
                schoolsByDistrict.set(school.district, new Set());
            }
            schoolsByDistrict.get(school.district).add(school.id);
        });
        
        filteredData.forEach(record => {
            if (!record.school_id || !record.district) return;
            if (!schoolsWithDataByDistrict.has(record.district)) {
                schoolsWithDataByDistrict.set(record.district, new Set());
            }
            schoolsWithDataByDistrict.get(record.district).add(record.school_id);
        });
        
        return Array.from(schoolsByDistrict.keys()).map(district => {
            const total = schoolsByDistrict.get(district).size;
            const withData = schoolsWithDataByDistrict.get(district)?.size || 0;
            const coverage = total > 0 ? (withData / total * 100) : 0;
            return { district, coverage };
        }).sort((a, b) => a.district.localeCompare(b.district));
    }, [filteredData, allData]);

    // 3. Топ школ
    const topSchoolsData = useMemo(() => {
        const schoolMap = new Map();
        
        filteredData.forEach(record => {
            if (!record.school_id) return;
            if (!schoolMap.has(record.school_id)) {
                const school = allData?.schools.find(s => s.id === record.school_id);
                schoolMap.set(record.school_id, {
                    name: school?.school_name || 'Неизвестная школа',
                    district: record.district,
                    users: new Set(),
                    teachers: new Set()
                });
            }
            const stats = schoolMap.get(record.school_id);
            stats.users.add(record.user_alias);
            if (record.role === 'teacher') stats.teachers.add(record.user_alias);
        });
        
        return Array.from(schoolMap.values())
            .map(s => ({
                name: s.name,
                district: s.district,
                users: s.users.size,
                teachers: s.teachers.size
            }))
            .sort((a, b) => b.users - a.users);
    }, [filteredData, allData]);

    // 4. Статистика по предметам
    const subjectsData = useMemo(() => {
        const subjectMap = new Map();
        
        filteredData.forEach(record => {
            if (!record.subject) return;
            if (!subjectMap.has(record.subject)) {
                subjectMap.set(record.subject, {
                    subject: record.subject,
                    users: new Set()
                });
            }
            subjectMap.get(record.subject).users.add(record.user_alias);
        });
        
        return Array.from(subjectMap.values())
            .map(s => ({
                subject: s.subject,
                users: s.users.size
            }))
            .sort((a, b) => b.users - a.users)
            .slice(0, 15);
    }, [filteredData]);

    // 5. Статистика по классам
    const classesData = useMemo(() => {
        const classMap = new Map();
        
        filteredData.forEach(record => {
            if (!record.class_level) return;
            const classLevel = parseInt(record.class_level);
            if (isNaN(classLevel) || classLevel < 1 || classLevel > 11) return;
            
            if (!classMap.has(classLevel)) {
                classMap.set(classLevel, {
                    class: classLevel,
                    users: new Set()
                });
            }
            classMap.get(classLevel).users.add(record.user_alias);
        });
        
        const data = Array.from(classMap.values())
            .sort((a, b) => a.class - b.class)
            .map(c => ({
                class: c.class,
                users: c.users.size
            }));
        
        let primary = 0, middle = 0, high = 0;
        let primaryUsers = 0, middleUsers = 0, highUsers = 0;
        
        data.forEach(c => {
            if (c.class >= 1 && c.class <= 4) {
                primary++;
                primaryUsers += c.users;
            } else if (c.class >= 5 && c.class <= 9) {
                middle++;
                middleUsers += c.users;
            } else if (c.class >= 10 && c.class <= 11) {
                high++;
                highUsers += c.users;
            }
        });
        
        return { data, levels: { primary, middle, high, primaryUsers, middleUsers, highUsers } };
    }, [filteredData]);

    // 6. Статистика по поставщикам
    const providersData = useMemo(() => {
        const providerMap = new Map();
        
        filteredData.forEach(record => {
            if (!record.provider) return;
            if (!providerMap.has(record.provider)) {
                providerMap.set(record.provider, {
                    provider: record.provider,
                    users: new Set(),
                    students: new Set(),
                    teachers: new Set()
                });
            }
            const stats = providerMap.get(record.provider);
            stats.users.add(record.user_alias);
            if (record.role === 'student') stats.students.add(record.user_alias);
            if (record.role === 'teacher') stats.teachers.add(record.user_alias);
        });
        
        return Array.from(providerMap.values())
            .map(p => ({
                provider: p.provider,
                users: p.users.size,
                students: p.students.size,
                teachers: p.teachers.size
            }))
            .sort((a, b) => b.users - a.users)
            .slice(0, topProvidersLimit);
    }, [filteredData, topProvidersLimit]);

    // 7. Динамика по дням
    const dailyData = useMemo(() => {
        const dayMap = new Map();
        
        filteredData.forEach(record => {
            if (!record.date) return;
            const dateKey = normalizeDate(record.date);
            if (!dateKey) return;
            
            if (!dayMap.has(dateKey)) {
                dayMap.set(dateKey, {
                    date: dateKey,
                    users: new Set(),
                    students: new Set(),
                    teachers: new Set()
                });
            }
            const stats = dayMap.get(dateKey);
            stats.users.add(record.user_alias);
            if (record.role === 'student') stats.students.add(record.user_alias);
            if (record.role === 'teacher') stats.teachers.add(record.user_alias);
        });
        
        return Array.from(dayMap.values())
            .map(d => ({
                date: d.date,
                users: d.users.size,
                students: d.students.size,
                teachers: d.teachers.size
            }))
            .sort((a, b) => a.date.localeCompare(b.date))
            .slice(-30);
    }, [filteredData]);

    // 8. Соотношение ролей
    const rolesData = useMemo(() => {
        const students = new Set();
        const teachers = new Set();
        
        filteredData.forEach(record => {
            if (record.role === 'student') students.add(record.user_alias);
            if (record.role === 'teacher') teachers.add(record.user_alias);
        });
        
        return { students: students.size, teachers: teachers.size };
    }, [filteredData]);

    // Форматирование дат
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        const parts = dateStr.split('-');
        if (parts.length === 3) {
            return `${parts[2]}.${parts[1]}`;
        }
        return dateStr;
    };

    // Данные для графиков (нейтральные цвета)
    const districtChartData = {
        labels: districtUsersData.map(d => d.district),
        datasets: [{
            label: 'Уникальные пользователи',
            data: districtUsersData.map(d => d.users),
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            borderWidth: 1,
            borderRadius: 6
        }]
    };

    const coverageChartData = {
        labels: coverageData.map(d => d.district),
        datasets: [{
            label: 'Охват школ (%)',
            data: coverageData.map(d => d.coverage),
            backgroundColor: coverageData.map(d => 
                d.coverage >= 80 ? colors.success : d.coverage >= 50 ? colors.warning : colors.danger
            ),
            borderWidth: 1,
            borderRadius: 6
        }]
    };

    const topSchoolsChartData = {
        labels: topSchoolsData.slice(0, topSchoolsLimit).map(s => s.name.length > 25 ? s.name.substring(0, 22) + '...' : s.name),
        datasets: [{
            label: 'Пользователи',
            data: topSchoolsData.slice(0, topSchoolsLimit).map(s => s.users),
            backgroundColor: colors.primary,
            borderColor: colors.primary,
            borderWidth: 1,
            borderRadius: 6
        }]
    };

    const topTeachersChartData = {
        labels: topSchoolsData.slice(0, topTeachersLimit).map(s => s.name.length > 25 ? s.name.substring(0, 22) + '...' : s.name),
        datasets: [{
            label: 'Учителя',
            data: topSchoolsData.slice(0, topTeachersLimit).map(s => s.teachers),
            backgroundColor: colors.secondary,
            borderColor: colors.secondary,
            borderWidth: 1,
            borderRadius: 6
        }]
    };

    const subjectsChartData = {
        labels: subjectsData.map(s => s.subject.length > 20 ? s.subject.substring(0, 17) + '...' : s.subject),
        datasets: [{
            label: 'Уникальные пользователи',
            data: subjectsData.map(s => s.users),
            backgroundColor: colors.purple,
            borderColor: colors.purple,
            borderWidth: 1,
            borderRadius: 6
        }]
    };

    const classesChartData = {
        labels: classesData.data.map(c => `${c.class} класс`),
        datasets: [{
            label: 'Уникальные пользователи',
            data: classesData.data.map(c => c.users),
            backgroundColor: colors.orange,
            borderColor: colors.orange,
            borderWidth: 1,
            borderRadius: 6
        }]
    };

    const providersChartData = {
        labels: providersData.map(p => p.provider.length > 25 ? p.provider.substring(0, 22) + '...' : p.provider),
        datasets: [
            {
                label: 'Пользователи',
                data: providersData.map(p => p.users),
                backgroundColor: colors.primary,
                borderColor: colors.primary,
                borderWidth: 1,
                borderRadius: 6
            },
            {
                label: 'Ученики',
                data: providersData.map(p => p.students),
                backgroundColor: colors.success,
                borderColor: colors.success,
                borderWidth: 1,
                borderRadius: 6
            },
            {
                label: 'Учителя',
                data: providersData.map(p => p.teachers),
                backgroundColor: colors.secondary,
                borderColor: colors.secondary,
                borderWidth: 1,
                borderRadius: 6
            }
        ]
    };

    const dailyChartData = {
        labels: dailyData.map(d => formatDate(d.date)),
        datasets: [
            {
                label: 'Пользователи',
                data: dailyData.map(d => d.users),
                borderColor: colors.primary,
                backgroundColor: `${colors.primary}20`,
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointBackgroundColor: colors.primary
            },
            {
                label: 'Ученики',
                data: dailyData.map(d => d.students),
                borderColor: colors.success,
                backgroundColor: `${colors.success}20`,
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointBackgroundColor: colors.success
            },
            {
                label: 'Учителя',
                data: dailyData.map(d => d.teachers),
                borderColor: colors.secondary,
                backgroundColor: `${colors.secondary}20`,
                fill: true,
                tension: 0.4,
                pointRadius: 3,
                pointBackgroundColor: colors.secondary
            }
        ]
    };

    const rolesChartData = {
        labels: ['Ученики', 'Учителя'],
        datasets: [{
            data: [rolesData.students, rolesData.teachers],
            backgroundColor: [colors.success, colors.primary],
            borderWidth: 0,
            borderRadius: 6
        }]
    };

    // Опции графиков
    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: { font: { size: 11, weight: 'normal' }, color: '#6c757d' }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        let label = context.dataset.label || '';
                        let value = context.raw;
                        return `${label}: ${value.toLocaleString()}`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                grid: { color: '#e9ecef' },
                ticks: { callback: (v) => v.toLocaleString(), font: { size: 10 }, color: '#6c757d' }
            },
            x: {
                grid: { display: false },
                ticks: { font: { size: 10 }, color: '#6c757d', maxRotation: 45, minRotation: 45 }
            }
        }
    };

    const horizontalOptions = {
        ...chartOptions,
        indexAxis: 'y',
        scales: {
            x: {
                beginAtZero: true,
                grid: { color: '#e9ecef' },
                ticks: { callback: (v) => v.toLocaleString(), font: { size: 10 }, color: '#6c757d' }
            },
            y: {
                grid: { display: false },
                ticks: { font: { size: 10 }, color: '#6c757d' }
            }
        }
    };

    const radarOptions = {
        ...chartOptions,
        scales: {
            r: {
                beginAtZero: true,
                max: 100,
                ticks: { callback: (v) => v + '%', stepSize: 20, color: '#6c757d' },
                grid: { color: '#e9ecef' },
                angleLines: { color: '#e9ecef' },
                pointLabels: { color: '#6c757d', font: { size: 10 } }
            }
        }
    };

    const pieOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { position: 'bottom', labels: { font: { size: 11 }, color: '#6c757d' } },
            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw.toLocaleString()}` } }
        }
    };

    // Экспорт
    const exportChartData = (chartId, data, title) => {
        if (!data || data.length === 0) return;
        const ws = XLSX.utils.json_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, title);
        XLSX.writeFile(wb, `${chartId}_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const exportAllChartsData = () => {
        const wb = XLSX.utils.book_new();
        
        const districtData = districtUsersData.map((d, i) => ({ '№': i + 1, 'Район': d.district, 'Пользователи': d.users }));
        const ws1 = XLSX.utils.json_to_sheet(districtData);
        XLSX.utils.book_append_sheet(wb, ws1, 'Районы');
        
        const coverageExp = coverageData.map((d, i) => ({ '№': i + 1, 'Район': d.district, 'Охват (%)': d.coverage.toFixed(1) }));
        const ws2 = XLSX.utils.json_to_sheet(coverageExp);
        XLSX.utils.book_append_sheet(wb, ws2, 'Охват');
        
        const topSchools = topSchoolsData.slice(0, 20).map((s, i) => ({ '№': i + 1, 'Школа': s.name, 'Пользователи': s.users }));
        const ws3 = XLSX.utils.json_to_sheet(topSchools);
        XLSX.utils.book_append_sheet(wb, ws3, 'Топ школ');
        
        const subjects = subjectsData.map((s, i) => ({ '№': i + 1, 'Предмет': s.subject, 'Пользователи': s.users }));
        const ws4 = XLSX.utils.json_to_sheet(subjects);
        XLSX.utils.book_append_sheet(wb, ws4, 'Предметы');
        
        const classes = classesData.data.map((c, i) => ({ '№': i + 1, 'Класс': c.class, 'Пользователи': c.users }));
        const ws5 = XLSX.utils.json_to_sheet(classes);
        XLSX.utils.book_append_sheet(wb, ws5, 'Классы');
        
        const providers = providersData.map((p, i) => ({ '№': i + 1, 'Поставщик': p.provider, 'Пользователи': p.users, 'Ученики': p.students, 'Учителя': p.teachers }));
        const ws6 = XLSX.utils.json_to_sheet(providers);
        XLSX.utils.book_append_sheet(wb, ws6, 'Поставщики');
        
        const daily = dailyData.map((d, i) => ({ '№': i + 1, 'Дата': d.date, 'Пользователи': d.users }));
        const ws7 = XLSX.utils.json_to_sheet(daily);
        XLSX.utils.book_append_sheet(wb, ws7, 'Динамика');
        
        XLSX.writeFile(wb, `ub_cok_visualization_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    const hasDailyData = dailyData.length > 0;
    const hasClassesData = classesData.data.length > 0;
    const hasProvidersData = providersData.length > 0;

    return (
        <div className="viz-container">
            <div className="viz-header">
                <h5><i className="bi bi-graph-up"></i> Визуализация данных</h5>
                <button className="viz-export-all" onClick={exportAllChartsData}>
                    <i className="bi bi-file-excel"></i> Экспорт всех данных
                </button>
            </div>

            {/* Ряд 1: Круговая диаграмма + Линейный график */}
            <div className="viz-row">
                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-pie-chart"></i> Соотношение учеников и учителей</span>
                    </div>
                    <div className="viz-card-body">
                        <div className="viz-chart" style={{ height: '280px' }}>
                            <Doughnut data={rolesChartData} options={pieOptions} />
                        </div>
                        <div className="viz-stats">
                            <div className="viz-stat">
                                <span className="badge" style={{ background: colors.success }}></span>
                                <span>Ученики: <strong>{rolesData.students.toLocaleString()}</strong></span>
                            </div>
                            <div className="viz-stat">
                                <span className="badge" style={{ background: colors.primary }}></span>
                                <span>Учителя: <strong>{rolesData.teachers.toLocaleString()}</strong></span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-graph-up"></i> Динамика активности по дням</span>
                        <button className="viz-export" onClick={() => exportChartData('daily', dailyData.map((d, i) => ({ '№': i + 1, 'Дата': d.date, 'Пользователи': d.users, 'Ученики': d.students, 'Учителя': d.teachers })), 'Динамика')} disabled={!hasDailyData}>
                            <i className="bi bi-file-excel"></i>
                        </button>
                    </div>
                    <div className="viz-card-body">
                        {hasDailyData ? (
                            <div className="viz-chart" style={{ height: '280px' }}>
                                <Line data={dailyChartData} options={chartOptions} />
                            </div>
                        ) : (
                            <div className="viz-empty">Нет данных для отображения динамики</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Ряд 2: Районы + Охват */}
            <div className="viz-row">
                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-map"></i> Пользователи по районам</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${studentsChartType === 'bar' ? 'active' : ''}`} onClick={() => setStudentsChartType('bar')}>Столбцы</button>
                                <button className={`viz-btn ${studentsChartType === 'horizontalBar' ? 'active' : ''}`} onClick={() => setStudentsChartType('horizontalBar')}>Полосы</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('studentsByDistrict', districtUsersData.map((d, i) => ({ '№': i + 1, 'Район': d.district, 'Пользователи': d.users })), 'Районы')}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        <div className="viz-chart" style={{ height: '350px' }}>
                            <Bar data={districtChartData} options={studentsChartType === 'horizontalBar' ? horizontalOptions : chartOptions} />
                        </div>
                    </div>
                </div>

                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-percent"></i> Охват школ по районам (%)</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${coverageChartType === 'bar' ? 'active' : ''}`} onClick={() => setCoverageChartType('bar')}>Столбцы</button>
                                <button className={`viz-btn ${coverageChartType === 'radar' ? 'active' : ''}`} onClick={() => setCoverageChartType('radar')}>Радар</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('coverageByDistrict', coverageData.map((d, i) => ({ '№': i + 1, 'Район': d.district, 'Охват (%)': d.coverage.toFixed(1) })), 'Охват')}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        <div className="viz-chart" style={{ height: '350px' }}>
                            {coverageChartType === 'radar' ? (
                                <Bar data={coverageChartData} options={radarOptions} />
                            ) : (
                                <Bar data={coverageChartData} options={chartOptions} />
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Ряд 3: Топ школы по пользователям + Топ школы по учителям */}
            <div className="viz-row">
                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-trophy"></i> Топ школ по пользователям</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${topSchoolsLimit === 5 ? 'active' : ''}`} onClick={() => setTopSchoolsLimit(5)}>5</button>
                                <button className={`viz-btn ${topSchoolsLimit === 10 ? 'active' : ''}`} onClick={() => setTopSchoolsLimit(10)}>10</button>
                                <button className={`viz-btn ${topSchoolsLimit === 15 ? 'active' : ''}`} onClick={() => setTopSchoolsLimit(15)}>15</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('topSchools', topSchoolsData.slice(0, topSchoolsLimit).map((s, i) => ({ '№': i + 1, 'Школа': s.name, 'Пользователи': s.users })), 'Топ школ')}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        <div className="viz-chart" style={{ height: '400px' }}>
                            <Bar data={topSchoolsChartData} options={horizontalOptions} />
                        </div>
                    </div>
                </div>

                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-person-badge"></i> Топ школ по учителям</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${topTeachersLimit === 5 ? 'active' : ''}`} onClick={() => setTopTeachersLimit(5)}>5</button>
                                <button className={`viz-btn ${topTeachersLimit === 10 ? 'active' : ''}`} onClick={() => setTopTeachersLimit(10)}>10</button>
                                <button className={`viz-btn ${topTeachersLimit === 15 ? 'active' : ''}`} onClick={() => setTopTeachersLimit(15)}>15</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('topSchoolsTeachers', topSchoolsData.slice(0, topTeachersLimit).map((s, i) => ({ '№': i + 1, 'Школа': s.name, 'Учителя': s.teachers })), 'Топ школ по учителям')}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        <div className="viz-chart" style={{ height: '400px' }}>
                            <Bar data={topTeachersChartData} options={horizontalOptions} />
                        </div>
                    </div>
                </div>
            </div>

            {/* Ряд 4: Предметы + Поставщики */}
            <div className="viz-row">
                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-book"></i> Топ предметов по пользователям</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${subjectsChartType === 'bar' ? 'active' : ''}`} onClick={() => setSubjectsChartType('bar')}>Столбцы</button>
                                <button className={`viz-btn ${subjectsChartType === 'horizontalBar' ? 'active' : ''}`} onClick={() => setSubjectsChartType('horizontalBar')}>Полосы</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('subjects', subjectsData.map((s, i) => ({ '№': i + 1, 'Предмет': s.subject, 'Пользователи': s.users })), 'Предметы')}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        <div className="viz-chart" style={{ height: '350px' }}>
                            <Bar data={subjectsChartData} options={subjectsChartType === 'horizontalBar' ? horizontalOptions : chartOptions} />
                        </div>
                    </div>
                </div>

                <div className="viz-card half">
                    <div className="viz-card-header">
                        <span><i className="bi bi-truck"></i> Топ поставщиков по пользователям</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${providersChartType === 'bar' ? 'active' : ''}`} onClick={() => setProvidersChartType('bar')}>Столбцы</button>
                                <button className={`viz-btn ${providersChartType === 'horizontalBar' ? 'active' : ''}`} onClick={() => setProvidersChartType('horizontalBar')}>Полосы</button>
                            </div>
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${topProvidersLimit === 5 ? 'active' : ''}`} onClick={() => setTopProvidersLimit(5)}>5</button>
                                <button className={`viz-btn ${topProvidersLimit === 10 ? 'active' : ''}`} onClick={() => setTopProvidersLimit(10)}>10</button>
                                <button className={`viz-btn ${topProvidersLimit === 15 ? 'active' : ''}`} onClick={() => setTopProvidersLimit(15)}>15</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('providers', providersData.map((p, i) => ({ '№': i + 1, 'Поставщик': p.provider, 'Пользователи': p.users, 'Ученики': p.students, 'Учителя': p.teachers })), 'Поставщики')} disabled={!hasProvidersData}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        {hasProvidersData ? (
                            <div className="viz-chart" style={{ height: '350px' }}>
                                <Bar data={providersChartData} options={providersChartType === 'horizontalBar' ? horizontalOptions : chartOptions} />
                            </div>
                        ) : (
                            <div className="viz-empty">Нет данных по поставщикам</div>
                        )}
                    </div>
                </div>
            </div>

            {/* Ряд 5: Классы */}
            <div className="viz-row">
                <div className="viz-card full">
                    <div className="viz-card-header">
                        <span><i className="bi bi-backpack"></i> Активность по классам</span>
                        <div className="viz-controls">
                            <div className="viz-btn-group">
                                <button className={`viz-btn ${classesChartType === 'bar' ? 'active' : ''}`} onClick={() => setClassesChartType('bar')}>Столбцы</button>
                                <button className={`viz-btn ${classesChartType === 'line' ? 'active' : ''}`} onClick={() => setClassesChartType('line')}>Линия</button>
                            </div>
                            <button className="viz-export" onClick={() => exportChartData('classes', classesData.data.map((c, i) => ({ '№': i + 1, 'Класс': c.class, 'Пользователи': c.users })), 'Классы')} disabled={!hasClassesData}>
                                <i className="bi bi-file-excel"></i>
                            </button>
                        </div>
                    </div>
                    <div className="viz-card-body">
                        {hasClassesData ? (
                            <>
                                <div className="viz-chart" style={{ height: '350px' }}>
                                    {classesChartType === 'line' ? (
                                        <Line data={classesChartData} options={chartOptions} />
                                    ) : (
                                        <Bar data={classesChartData} options={chartOptions} />
                                    )}
                                </div>
                                <div className="viz-levels">
                                    <div className="viz-level primary">
                                        <span className="badge" style={{ background: colors.primary }}></span>
                                        <span>Начальная (1-4): <strong>{classesData.levels.primary} классов</strong></span>
                                        <span className="count">{classesData.levels.primaryUsers.toLocaleString()} пользователей</span>
                                    </div>
                                    <div className="viz-level middle">
                                        <span className="badge" style={{ background: colors.success }}></span>
                                        <span>Основная (5-9): <strong>{classesData.levels.middle} классов</strong></span>
                                        <span className="count">{classesData.levels.middleUsers.toLocaleString()} пользователей</span>
                                    </div>
                                    <div className="viz-level high">
                                        <span className="badge" style={{ background: colors.warning }}></span>
                                        <span>Старшая (10-11): <strong>{classesData.levels.high} классов</strong></span>
                                        <span className="count">{classesData.levels.highUsers.toLocaleString()} пользователей</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="viz-empty">Нет данных по классам</div>
                        )}
                    </div>
                </div>
            </div>

            <div className="viz-footer">
                <i className="bi bi-info-circle"></i> Графики построены на основе уникальных пользователей. Данные учитывают выбранные фильтры.
            </div>
        </div>
    );
};

export default Visualization;